
import { useState, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Image as ImageIcon, Film, File } from "lucide-react";
import { nanoid } from "nanoid";
import { Image } from "@/lib/types";
import { generateSlug } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface ImageUploaderProps {
  onUpload: (image: Image) => void;
  onSuccess?: () => void; // New callback for when upload completes successfully!
}

const ImageUploader = ({ onUpload, onSuccess }: ImageUploaderProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mediaType, setMediaType] = useState<"image" | "video" | "gif" | "document">("image");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const determineMediaType = (file: File): "image" | "video" | "gif" | "document" => {
    const type = file.type;
    
    if (type.startsWith('video/')) {
      return 'video';
    } else if (type === 'image/gif') {
      return 'gif';
    } else if (type.startsWith('image/')) {
      return 'image';
    } else {
      return 'document';
    }
  };

  const handleFile = (file: File) => {
    // Accept all file types - no restrictions
    
    // Check file size (limit to 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      toast.error('File is too large. Maximum size is 50MB');
      return;
    }

    setFile(file);
    const type = determineMediaType(file);
    setMediaType(type);

    // Create preview
    setName(file.name.split('.')[0]);
    
    if (type === 'video') {
      // For videos, create a URL for preview
      const videoUrl = URL.createObjectURL(file);
      setPreview(videoUrl);
    } else if (type === 'image' || type === 'gif') {
      // For images, use FileReader
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target) {
          setPreview(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    } else {
      // For other file types, just set a placeholder
      setPreview('file-preview');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!preview || !file || !user) {
      toast.error('Please select a file first');
      return;
    }

    setIsUploading(true);
    
    try {
      // Generate a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${nanoid()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;
      
      // Upload file to Supabase Storage
      const { error: uploadError, data } = await supabase.storage
        .from('images')
        .upload(filePath, file, {
          contentType: file.type // Set the proper content type
        });
        
      if (uploadError) {
        throw uploadError;
      }
      
      // Get public URL for the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);
        
      // Generate a unique slug for the image
      const slug = generateSlug(name || 'untitled');
      
      // Insert image metadata into the database
      const { error: dbError, data: imageData } = await supabase
        .from('images')
        .insert({
          user_id: user.id,
          name: name || 'Untitled',
          description,
          slug,
          storage_path: filePath,
          content_type: file.type,
          views: 0
        })
        .select()
        .single();
        
      if (dbError) {
        throw dbError;
      }
      
      // Create image object for local state
      const newImage: Image = {
        id: imageData.id,
        name: name || 'Untitled',
        description,
        url: publicUrl,
        slug,
        createdAt: new Date(imageData.created_at).getTime(),
        views: 0,
        contentType: file.type // Add content type
      };
      
      onUpload(newImage);
      
      // Reset form
      setPreview(null);
      setFile(null);
      setName("");
      setDescription("");
      setMediaType("document");
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      toast.success('File uploaded successfully');
      
      // Clean up any object URLs we created
      if (mediaType === 'video' && preview) {
        URL.revokeObjectURL(preview);
      }

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Error uploading file');
    } finally {
      setIsUploading(false);
    }
  };

  const renderPreview = () => {
    if (!preview) return null;
    
    if (mediaType === 'video') {
      return (
        <video 
          src={preview} 
          controls 
          className="max-h-full max-w-full" 
          autoPlay 
          muted 
          loop
        />
      );
    } else if (preview === 'file-preview') {
      // Show file name and type for documents  
      const fileExtension = file?.name.split('.').pop()?.toUpperCase() || 'FILE';
      return (
        <div className="flex flex-col items-center justify-center text-white/90 p-8 space-y-3">
          <File className="h-16 w-16 text-primary/70" />
          <div className="text-center">
            <p className="text-lg font-medium">{file?.name}</p>
            <p className="text-sm text-white/60 mt-1">{fileExtension} File</p>
          </div>
        </div>
      );
    } else {
      return (
        <img
          src={preview}
          alt="Preview"
          className="max-h-full max-w-full"
        />
      );
    }
  };

  return (
    <Card className="glass-card">
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {!preview ? (
            <div
              className={`drop-zone flex flex-col items-center justify-center cursor-pointer ${isDragging ? 'active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-12 w-12 mb-3 text-primary/70" />
              <h3 className="text-lg font-medium mb-1">Drag and drop files</h3>
              <p className="text-sm text-white/60 mb-1">Any file type up to 50MB</p>
              <p className="text-sm text-white/60 mb-3">or click to browse files</p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="aspect-video bg-black/20 rounded-lg overflow-hidden flex items-center justify-center">
                {renderPreview()}
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    File Name
                  </label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter name"
                    className="bg-black/30 border-white/10"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Description (optional)</label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter description"
                    className="bg-black/30 border-white/10"
                  />
                </div>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload File'}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setPreview(null);
                    setFile(null);
                    setMediaType("document");
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                    // Clean up any object URLs we created
                    if (mediaType === 'video' && preview) {
                      URL.revokeObjectURL(preview);
                    }
                  }}
                  className="border-white/10 hover:bg-white/5"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default ImageUploader;
